import { useTranslation } from 'react-i18next';

import {
  CallTranscriptMessageSource,
  CallTranscriptMessageType,
  orderMessages,
} from '@ngsos/portal-core/transcript';
import type { CallTranscriptMessage, CallTranscriptMessageSource as MessageSource } from '@ngsos/portal-core/transcript';
import type { ParsedTranscript } from '@ngsos/portal-core/portal';
import { formatOffset } from './formatOffsets';

export type CallTranscriptMessageListProps = {
  transcript: ParsedTranscript;
};

function getBubbleCssClass(source: MessageSource, type: CallTranscriptMessage['type']): string {
  const sourceCssClass =
    source === CallTranscriptMessageSource.Caller
      ? 'call-transcript-message--caller'
      : 'call-transcript-message--callee';

  const statusCssClass =
    type === CallTranscriptMessageType.InterimTranscript
      ? 'call-transcript-message--in-progress'
      : 'call-transcript-message--final';

  return `${sourceCssClass} ${statusCssClass}`;
}

function getOffsetLabel(message: CallTranscriptMessage): string {
  return `${formatOffset(message.startOffset)} - ${formatOffset(message.endOffset)}`;
}

function getRowCssClass(source: MessageSource): string {
  return source === CallTranscriptMessageSource.Caller
    ? 'call-transcript-message-list__row--caller'
    : 'call-transcript-message-list__row--callee';
}

function getOriginalContentTooltip(message: CallTranscriptMessage): string | undefined {
  return message.type === CallTranscriptMessageType.EnhancedTranscript ? message.originalContent : undefined;
}

function getTooltipTabIndex(message: CallTranscriptMessage): number | undefined {
  return message.type === CallTranscriptMessageType.EnhancedTranscript ? 0 : undefined;
}

export function CallTranscriptMessageList({ transcript }: CallTranscriptMessageListProps) {
  const { t } = useTranslation();

  const getSourceLabel = (source: MessageSource): string =>
    source === CallTranscriptMessageSource.Caller ? t('CallTranscript_Source_Caller') : t('CallTranscript_Source_Callee');

  const stableMessages = orderMessages(
    transcript.messages.filter((message) => message.type !== CallTranscriptMessageType.InterimTranscript),
  );

  const latestInterimBySource = new Map<MessageSource, CallTranscriptMessage>();
  for (const message of transcript.messages) {
    if (message.type !== CallTranscriptMessageType.InterimTranscript || (message.content ?? '').trim() === '') {
      continue;
    }
    const current = latestInterimBySource.get(message.source);
    if (
      current === undefined ||
      message.endOffset > current.endOffset ||
      (message.endOffset === current.endOffset && message.startOffset > current.startOffset)
    ) {
      latestInterimBySource.set(message.source, message);
    }
  }
  const inProgressMessages = orderMessages(latestInterimBySource.values());

  return (
    <div className="call-transcript-message-list">
      <div className="call-transcript-message-list__history">
        {stableMessages.map((message, index) => (
          <div
            key={message.messageId ?? `${message.source}-${message.startOffset}-${index}`}
            className={`call-transcript-message-list__row ${getRowCssClass(message.source)}`}
          >
            <div
              className={`call-transcript-message ${getBubbleCssClass(message.source, message.type)}`}
              title={getOriginalContentTooltip(message)}
              tabIndex={getTooltipTabIndex(message)}
            >
              <div className="call-transcript-message__meta">
                <span>{getSourceLabel(message.source)}</span>
                <span>{getOffsetLabel(message)}</span>
                {message.type === CallTranscriptMessageType.EnhancedTranscript && (
                  <span className="call-transcript-message__status call-transcript-message__status--enhanced">
                    {t('CallTranscript_Message_Enhanced')}
                  </span>
                )}
              </div>
              <div className="call-transcript-message__content">{message.content}</div>
            </div>
          </div>
        ))}
      </div>

      {inProgressMessages.length > 0 && (
        <div className="call-transcript-message-list__in-progress">
          {inProgressMessages.map((message, index) => (
            <div
              key={message.messageId ?? `${message.source}-${message.startOffset}-${index}`}
              className={`call-transcript-message-list__row ${getRowCssClass(message.source)}`}
            >
              <div className={`call-transcript-message ${getBubbleCssClass(message.source, message.type)}`}>
                <div className="call-transcript-message__meta">
                  <span>{getSourceLabel(message.source)}</span>
                  <span>{getOffsetLabel(message)}</span>
                  <span className="call-transcript-message__status">{t('CallTranscript_Status_InProgress')}</span>
                </div>
                <div className="call-transcript-message__content">{message.content}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
